import { useContext, useEffect, useState } from "react";

import { useRouter } from "next/router";

import { Button, Col } from "antd";
import cookie from "js-cookie";
import useTranslation from "next-translate/useTranslation";

import StaticIntlTranslator from "shared/components/atoms/StaticIntlTranslator";
import AuthContext from "shared/providers/context/AuthContext";
import GlobalContext from "shared/providers/context/GlobalContext";
import { clearCourseCache } from "shared/utils/clearCourseCache";
import setProductSession from "shared/utils/setProductSession";

import switchContext from "../../utils/switchContext";
import { Container, Tag } from "./styles";

const ItemsToBuyIndividuallyOrByPlans = ({
  entity,
  context,
  isContentScreen = false,
  isProductBundleScreen = false,
  isCourseLandingPageCard = false,
  isCourseLandingPageFooter = false,
}: ItemsToBuyIndividuallyOrByPlansProps) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { loggedUser } = useContext(AuthContext);

  const { state, setState } = useContext(GlobalContext);

  const params = new URLSearchParams(window.location.search);

  const themeBuyButtonsColor =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsColor;

  const themeBuyButtonsOption =
    state.tenancy?.settings?.["advanced-theme"]?.themeBuyButtonsOption;

  const [publishedPlans, setPublishedPlans] = useState([]);

  const installmentsFree = entity.installments.filter(
    (item: { fee: number }) => item.fee === 0,
  );
  const installmentUpTo = entity?.installments
    .slice(-1)[0]
    ?.value?.toFixed(2)
    .toString()
    .replace(".", ",");

  useEffect(() => {
    if (entity?.plans?.length > 0) {
      const activePlans = entity.plans.filter(
        (plan: { status: string }) => plan.status === "published",
      );
      setPublishedPlans(activePlans);
    }
  }, [entity]);

  const goToCheckout = (
    entityType: string,
    paramType: string,
    product: any,
  ) => {
    setProductSession(entityType, paramType, product);

    if (router.query.course_slug)
      clearCourseCache(router.query.course_slug as string);

    const checkoutOnePageDirectLinkSettingsOthers =
      state?.tenancy?.settings?.others?.checkoutOnePageProductPage === "1";
    const checkoutOnePageDirectLinkSettingsSite =
      state?.tenancy?.settings?.site?.checkoutOnePageProductPage === "1";

    const isCheckoutOnePage =
      checkoutOnePageDirectLinkSettingsOthers ||
      checkoutOnePageDirectLinkSettingsSite;

    if (isCheckoutOnePage) {
      router.push(`/payment/p/${product?.checkout_uuid}`);
    } else {
      if (!loggedUser) {
        const currentTime = new Date().getTime();
        const fiveMinutes = 7 * 60 * 1000;

        const expiresTime = new Date(currentTime + fiveMinutes);

        cookie.set("checkout_redirect", `/payment/p/${product.checkout_uuid}`, {
          expires: expiresTime,
        });

        return router.push("/authentication");
      }

      router.push(`/payment/p/${product?.checkout_uuid}`);
    }
  };

  const handlePurchase = (singlePlan = false) => {
    const { paramType: paramTypeCtx, entityType: entityTypeCtx } =
      switchContext({ context });

    let paramType = paramTypeCtx;
    let entityType = entityTypeCtx;
    let checkout_uuid = entity.checkout_uuid;

    if (singlePlan) {
      entityType = "plan";
      paramType = "plan_id";
      checkout_uuid = publishedPlans[0].checkout_uuid;

      setProductSession(entityType, paramType, {
        ...publishedPlans[0],
        description: entity.description,
        maintenance: entity.maintenance,
      });
    } else {
      setProductSession(entityType, paramType, entity);
    }

    if (router.query.course_slug)
      clearCourseCache(router.query.course_slug as string);

    const checkoutOnePageDirectLinkSettingsOthers =
      state?.tenancy?.settings?.others?.checkoutOnePageProductPage === "1";
    const checkoutOnePageDirectLinkSettingsSite =
      state?.tenancy?.settings?.site?.checkoutOnePageProductPage === "1";

    const isCheckoutOnePage =
      checkoutOnePageDirectLinkSettingsOthers ||
      checkoutOnePageDirectLinkSettingsSite;

    if (isCheckoutOnePage) {
      router.push(`/payment/p/${checkout_uuid}`);
    } else {
      if (!loggedUser) {
        const currentTime = new Date().getTime();
        const fiveMinutes = 7 * 60 * 1000;

        const expiresTime = new Date(currentTime + fiveMinutes);

        cookie.set("checkout_redirect", `/payment/p/${checkout_uuid}`, {
          expires: expiresTime,
        });

        return router.push("/authentication");
      }

      router.push(`/payment/p/${checkout_uuid}`);
    }
  };

  const handleOpenPurchaseOptions = () => {
    setState((prev) => ({
      ...prev,
      enrollments: {
        paymentOptions: {
          entity,
          context,
          handlePurchase: goToCheckout,
          show: true,
        },
      },
    }));
  };

  useEffect(() => {
    if (params.has("buy")) handlePurchase();
  }, [params]);

  return (
    <Container
      isContentScreen={isContentScreen}
      isProductBundleScreen={isProductBundleScreen}
      isCourseLandingPageCard={isCourseLandingPageCard}
      isCourseLandingPageFooter={isCourseLandingPageFooter}
      customButtonColor={themeBuyButtonsColor}
      themeBuyButtonsOption={themeBuyButtonsOption}
    >
      {publishedPlans?.length > 0 ? (
        <>
          <div className="prices-plus-info">
            {(isCourseLandingPageCard || isContentScreen) && (
              <>
                {(publishedPlans.length > 1 || !!entity?.individual_sale) && (
                  <h5 className="label">
                    <StaticIntlTranslator id="common:chooseOnePaymentOption" />
                  </h5>
                )}

                <span className="starting-at outside-footer">
                  {publishedPlans?.length <= 1 && !entity?.individual_sale ? (
                    <StaticIntlTranslator id="common:subscribeFor" />
                  ) : (
                    <StaticIntlTranslator id="common:startingAt" />
                  )}
                </span>
              </>
            )}

            <div className="prices">
              {(isCourseLandingPageFooter || isProductBundleScreen) && (
                <span>
                  {publishedPlans?.length <= 1 && !entity?.individual_sale ? (
                    <StaticIntlTranslator id="common:subscribeFor" />
                  ) : (
                    <StaticIntlTranslator id="common:startingAt" />
                  )}
                </span>
              )}

              <section className="price">{entity.lowest_price}</section>
            </div>
          </div>

          <Button
            type="primary"
            className="buy-btn buy-or-subscribe"
            onClick={() => {
              if (isCourseLandingPageFooter) {
                const element = document.querySelector(".course-thumb-img");

                if (element) {
                  element.scrollIntoView({
                    block: "start",
                    behavior: "smooth",
                  });
                  window.scroll(0, 0);
                }
              } else if (
                publishedPlans.length === 1 &&
                !entity?.individual_sale
              ) {
                handlePurchase(true);
              } else {
                handleOpenPurchaseOptions();
              }
            }}
          >
            {entity?.individual_sale ? (
              <StaticIntlTranslator id="common:buyOrSubscribe" />
            ) : (
              <StaticIntlTranslator id="common:subscribeNow" />
            )}
          </Button>
        </>
      ) : (
        <>
          <div className="prices-plus-info">
            {entity?.individual_sale !== 0 ? (
              <div className="prices">
                {entity?.use_before_price && (
                  <div className="before-price-content">
                    <Col xs={14} sm={14} md={13} lg={13} xl={13} xxl={13}>
                      {entity?.before_price_info?.value !== 0 && (
                        <h5 className="before-price">
                          <del>{entity.before_price}</del>
                        </h5>
                      )}
                    </Col>

                    <Col xs={14} sm={14} md={13} lg={13} xl={13} xxl={13}>
                      {!isCourseLandingPageFooter &&
                        entity?.before_price_info?.value > 0 && (
                          <Tag>
                            {Math.floor(
                              100 -
                                (entity.price_info.value * 100) /
                                  entity.before_price_info.value,
                            )}
                            <StaticIntlTranslator id="common:percentOff" />
                          </Tag>
                        )}
                    </Col>
                  </div>
                )}

                <div>
                  <h5 className="price">{entity.price}</h5>
                  {!isCourseLandingPageFooter && (
                    <span className="in-cash">
                      <StaticIntlTranslator id="common:inCash" />
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <h4 className="subs-closed">
                <StaticIntlTranslator id="common:registrationForThisContentIsClosed" />
              </h4>
            )}

            {!isCourseLandingPageFooter && entity?.individual_sale !== 0 && (
              <div className="info-container">
                {entity?.has_installments && (
                  <span className="installments">
                    <StaticIntlTranslator id="common:upTo" />
                    {` ${entity.total_installments}x ${
                      entity.total_installments === installmentsFree.length + 1
                        ? `${installmentUpTo || ""} ${t("common:interestFree")}`
                        : new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(installmentUpTo?.replace(",", "."))
                    }`}
                  </span>
                )}

                {context === "plan" && (
                  <span className="plan-interval">
                    ({entity?.interval_description})
                  </span>
                )}
              </div>
            )}
          </div>

          {entity?.individual_sale !== 0 && (
            <Button
              type="primary"
              className="buy-btn"
              onClick={() => handlePurchase()}
            >
              {context === "plan" ? (
                <StaticIntlTranslator id="common:subscribeNow" />
              ) : (
                <StaticIntlTranslator id="common:buyNow" />
              )}
            </Button>
          )}
        </>
      )}
    </Container>
  );
};

interface ItemsToBuyIndividuallyOrByPlansProps {
  entity: any;
  context: string;
  isContentScreen?: boolean;
  isProductBundleScreen?: boolean;
  isCourseLandingPageCard?: boolean;
  isCourseLandingPageFooter?: boolean;
}

export default ItemsToBuyIndividuallyOrByPlans;
